import { useEffect, useState } from "react";
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog } from "lucide-react";

// Open-Meteo is free and keyless, but only forecasts ~16 days out -- the section
// simply doesn't render outside that window instead of showing stale/missing data.
const FORECAST_HORIZON_DAYS = 15;

function weatherFor(code) {
  if (code === 0) return { Icon: Sun, label: "Sereno" };
  if ([1, 2, 3].includes(code)) return { Icon: Cloud, label: "Parzialmente nuvoloso" };
  if ([45, 48].includes(code)) return { Icon: CloudFog, label: "Nebbia" };
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return { Icon: CloudRain, label: "Pioggia" };
  if ([71, 73, 75, 77, 85, 86].includes(code)) return { Icon: CloudSnow, label: "Neve" };
  if ([95, 96, 99].includes(code)) return { Icon: CloudLightning, label: "Temporale" };
  return { Icon: Cloud, label: "Variabile" };
}

export default function WeatherForecast({ wedding }) {
  const [forecast, setForecast] = useState(null);
  const eventDate = new Date(wedding.data_evento);
  const daysUntil = (eventDate.getTime() - Date.now()) / 86_400_000;
  const inWindow = daysUntil >= -0.5 && daysUntil <= FORECAST_HORIZON_DAYS;

  useEffect(() => {
    if (!inWindow || !wedding.location) return;
    let cancelled = false;
    const dateStr = eventDate.toISOString().slice(0, 10);
    (async () => {
      try {
        // Open-Meteo's own geocoder only knows cities/towns, not venue names (a
        // wedding "location" is usually "Villa Bortolazzi", not "Trento") --
        // Nominatim (OpenStreetMap) indexes actual points of interest and resolves
        // those correctly, so it's used for geocoding while Open-Meteo still
        // supplies the actual forecast.
        const geoRes = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(wedding.location)}&format=json&limit=1`,
          { headers: { Accept: "application/json" } },
        );
        const geo = await geoRes.json();
        const place = geo?.[0];
        if (!place) return;
        const wRes = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${place.lat}&longitude=${place.lon}&daily=weathercode,temperature_2m_max,temperature_2m_min&timezone=auto&start_date=${dateStr}&end_date=${dateStr}`,
        );
        const w = await wRes.json();
        const code = w.daily?.weathercode?.[0];
        const max = w.daily?.temperature_2m_max?.[0];
        const min = w.daily?.temperature_2m_min?.[0];
        if (!cancelled && code != null) setForecast({ code, max, min, placeName: place.name || wedding.location });
      } catch {
        // silently hide the section on any failure (network, parsing, no match, ecc.)
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inWindow, wedding.location, wedding.data_evento]);

  if (!inWindow || !forecast) return null;
  const { Icon, label } = weatherFor(forecast.code);

  return (
    <section className="py-10 px-6 sm:px-12" data-testid="weather-section">
      <div className="max-w-md mx-auto flex items-center gap-4 border rounded-2xl p-5" style={{ borderColor: "#e3ddd2" }}>
        <Icon size={32} style={{ color: "var(--wed-primary)" }} className="shrink-0" />
        <div>
          <p className="text-xs text-neutral-400">Previsioni per {forecast.placeName}</p>
          <p className="font-wed text-xl">{label} · {Math.round(forecast.min)}°–{Math.round(forecast.max)}°</p>
        </div>
      </div>
    </section>
  );
}
